import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { useDashboardStore } from '../../store/dashboardStore';
import { formatInTimeZone } from 'date-fns-tz';
import { useMobileEdgeSwipeBack } from '../../hooks/useMobileEdgeSwipeBack';

/**
 * 日付が過去かどうかを判定（JST基準、日付のみで比較）
 */
const isPastDate = (date: Date | null): boolean => {
  if (!date) return false;
  const timeZone = 'Asia/Tokyo';
  const now = new Date();
  
  // JSTで日付をフォーマット（時刻は無視）
  const dateStr = formatInTimeZone(date, timeZone, 'yyyy-MM-dd');
  const todayStr = formatInTimeZone(now, timeZone, 'yyyy-MM-dd');
  
  return dateStr < todayStr;
};

interface SchedulePanelProps {
  isMobile?: boolean;
}

export const SchedulePanel: React.FC<SchedulePanelProps> = ({ isMobile = false }) => {
  const panelVariants: Variants = isMobile 
    ? {
        hidden: { x: '100%', opacity: 1 },
        hiddenBack: { x: '-100%', opacity: 1 },
        visible: { x: 0, opacity: 1 },
        exitBack: {
          x: '100%',
          opacity: 0,
          transition: { duration: 0.35, ease: 'easeOut' },
        },
        exitForward: { x: '-100%' },
      }
    : {
        hidden: { x: '-100%' },  // デスクトップ版は左から
        visible: { x: 0 },
      };

  const { setScheduleSelectedTime, loadScheduleAppointments, scheduleAppointmentsByTime, scheduleSelectedStaffId, selectedDate, allPatients, allStaff, scheduleSelectedTime, subscribeSchedule, cancelAppointmentAtSelectedSlot, showSettingsDatePanel, schedulePanelSource, setShowSchedulePanel, setShowSettingsDatePanel, isNavigatingBack, setIsNavigatingBack, navigateBackToDatePanel, showSchedulePatientListPanel } = useDashboardStore();
  const [expandedTime, setExpandedTime] = useState<string | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState<{ time: string; patientName: string } | null>(null);
  const [cancelPending, setCancelPending] = useState(false);

  const panelContext: 'settings' | 'workflow' = showSettingsDatePanel ? 'settings' : 'workflow';
  
  // 選択された日付が過去かどうかを判定
  const isSelectedDatePast = isPastDate(selectedDate);

  // 9:00〜18:00 を30分刻みで生成
  const slots: string[] = [];
  for (let m = 9 * 60; m < 18 * 60; m += 30) {
    const hh = Math.floor(m / 60).toString().padStart(2, '0');
    const mm = (m % 60).toString().padStart(2, '0');
    slots.push(`${hh}:${mm}`);
  }

  useEffect(() => {
    loadScheduleAppointments();
  }, [loadScheduleAppointments, scheduleSelectedStaffId, selectedDate, allPatients.length]);

  useEffect(() => {
    subscribeSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedDateYmd = selectedDate
    ? formatInTimeZone(selectedDate, 'Asia/Tokyo', 'yyyy-MM-dd')
    : '';

  useEffect(() => {
    setExpandedTime(null);
    setCancelConfirm(null);
    setCancelPending(false);
    setScheduleSelectedTime(null);
  }, [selectedDateYmd, scheduleSelectedStaffId, setScheduleSelectedTime]);

  const formatJPDate = (d: Date | null) => {
    if (!d) return '';
    const months = d.getMonth() + 1;
    const day = d.getDate();
    const week = ['日', '月', '火', '水', '木', '金', '土'][d.getDay()];
    return `${months}月${day}日（${week}）`;
  };

  const staffName = (() => {
    if (!scheduleSelectedStaffId) return '';
    const s = allStaff?.find((st) => String(st.id) === String(scheduleSelectedStaffId));
    return s?.name ? `${s.name}さん` : '';
  })();

  const scheduleSwipeRef = useRef<HTMLDivElement>(null);
  const handleScheduleEdgeBack = useCallback(() => {
    if (schedulePanelSource === 'settings') {
      setIsNavigatingBack(true);
      setShowSchedulePanel(false);
      setShowSettingsDatePanel(true);
    } else {
      navigateBackToDatePanel();
    }
  }, [schedulePanelSource, setIsNavigatingBack, setShowSchedulePanel, setShowSettingsDatePanel, navigateBackToDatePanel]);

  const { swipeStyle: scheduleSwipeStyle } = useMobileEdgeSwipeBack(scheduleSwipeRef, {
    isMobile: !!isMobile,
    enabled: !!isMobile && !showSchedulePatientListPanel && !cancelConfirm,
    onNavigateBack: handleScheduleEdgeBack,
  });

  return (
    <>
      {cancelConfirm && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
          onClick={() => {
            if (!cancelPending) setCancelConfirm(null);
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-5 max-w-xs mx-4 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-2 text-gray-800">
              <p className="font-semibold text-gray-900">この予約をキャンセルしますか？</p>
              <p className="text-sm">
                {cancelConfirm.patientName}（{cancelConfirm.time}）
              </p>
            </div>
            <div className="mt-4 flex flex-nowrap justify-center gap-3">
              <button
                type="button"
                disabled={cancelPending}
                onClick={() => setCancelConfirm(null)}
                className="shrink-0 whitespace-nowrap py-2 px-4 border border-gray-300 rounded-md text-gray-800 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                閉じる
              </button>
              <button
                type="button"
                disabled={cancelPending}
                onClick={async () => {
                  const slot = cancelConfirm;
                  if (!slot) return;
                  setCancelPending(true);
                  setScheduleSelectedTime(slot.time);
                  const success = await cancelAppointmentAtSelectedSlot();
                  setCancelPending(false);
                  setCancelConfirm(null);
                  if (success) {
                    setExpandedTime(null);
                    setScheduleSelectedTime(null);
                  }
                }}
                className="shrink-0 whitespace-nowrap py-2 px-5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {cancelPending ? '処理中…' : 'キャンセルする'}
              </button>
            </div>
          </div>
        </div>
      )}
    <div
      ref={scheduleSwipeRef}
      style={isMobile ? scheduleSwipeStyle : undefined}
      className={isMobile ? 'fixed top-0 left-0 right-0 bottom-0 pt-[var(--header-actual,var(--header-total))] z-[42]' : 'contents'}
    >
    <motion.div
      variants={panelVariants}
      initial={isMobile && isNavigatingBack ? "hiddenBack" : "hidden"}
      animate="visible"
      exit={isMobile ? (isNavigatingBack ? "exitBack" : "exitForward") : undefined}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={isMobile 
        ? "h-full w-full bg-white flex flex-col shadow-lg" 
        : `panel-col ${panelContext === 'settings' ? 'panel-col--settings' : 'panel-col--workflow'} z-[5] bg-white border-r flex flex-col shadow-lg`
      }
    >
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {isMobile ? (
            <button
              onClick={() => {
                // スマホでは設定の日付選択後に showSettingsDatePanel が false になるため、ソースで判定する
                if (schedulePanelSource === 'settings') {
                  setIsNavigatingBack(true);
                  setShowSchedulePanel(false);
                  setShowSettingsDatePanel(true);
                } else {
                  navigateBackToDatePanel();
                }
              }}
              className="text-lg font-semibold text-gray-800 hover:text-gray-600 transition-colors cursor-pointer"
            >
              スケジュール
            </button>
          ) : (
            <h2 className="text-lg font-semibold text-gray-800">スケジュール</h2>
          )}
        </div>
      </div>
      <div className="px-4 py-2 text-sm text-red-800 font-semibold">
        {`${formatJPDate(selectedDate)}${staffName ? ' ' + staffName : ''}のスケジュール`}
      </div>
      <div className={`flex-1 px-2 py-3 text-gray-700 overflow-y-auto ${isMobile ? 'pb-[var(--bottom-menu-total)]' : ''}`}>
        <div className="space-y-2">
          {slots.map((time) => (
            <div key={`${scheduleSelectedStaffId || 'no-staff'}-${time}`}> 
              {(() => {
                const info = scheduleAppointmentsByTime[time];
                if (!info) {
                  // 未予約: 空のカード（クリックで予約登録パネル）
                  // 過去の日付または診療フローの場合は選択不可（予約登録は設定フローからのみ）
                  const isWorkflow = schedulePanelSource === 'workflow'; // PC/スマホ共通：診療フローでは予約登録不可
                  const isDisabled = isSelectedDatePast || isWorkflow;
                  return (
                    <button
                      type="button"
                      onClick={(e) => {
                        // 過去の日付または診療フローの場合は早期リターン
                        if (isDisabled) return;
                        // 設定フローでは「閲覧（申し送り）」が開いたままになりやすいので、空き枠選択時に閉じる
                        // スマホ：スケジュールをいったん閉じて予約登録を開き、DashboardLayout の AnimatePresence で他パネル同様に前後スライドを同期（重ね表示にしない）
                        useDashboardStore.setState((state) => ({
                          showDetailPanel: false,
                          selectedPatientId: null,
                          detailPanelRevision: state.detailPanelRevision + 1,
                          scheduleSelectedTime: time,
                          ...(isMobile
                            ? {
                                isNavigatingBack: false,
                                showSchedulePanel: false,
                                schedulePanelRevision: state.schedulePanelRevision + 1,
                                showSchedulePatientListPanel: true,
                              }
                            : {
                                showSchedulePatientListPanel: true,
                              }),
                        }));
                        setExpandedTime(null);
                        (e.currentTarget as HTMLButtonElement).blur();
                      }}
                      disabled={isDisabled}
                      className={`w-full p-3 rounded-md text-left transition-colors appearance-none touch-manipulation [-webkit-tap-highlight-color:transparent] focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1 ${
                        isDisabled
                          ? 'opacity-50 cursor-not-allowed bg-gray-50 border border-transparent'
                          : scheduleSelectedTime === time
                            ? 'bg-gray-50 border-2 border-gray-400'
                            : 'bg-white border border-transparent [@media(hover:hover)]:hover:bg-gray-100 active:bg-gray-100'
                      }`}
                      aria-label={isDisabled ? (isWorkflow ? `時間枠 ${time} は選択できません（診療フローでは予約登録できません）` : `時間枠 ${time} は選択できません（過去の日付）`) : `時間枠 ${time} を選択`}
                      aria-disabled={isDisabled}
                    >
                      <div className="grid grid-cols-[56px_1fr] gap-x-4 items-center">
                        <span className={`text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-500'}`}>{time}</span>
                        <span className={`text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-400'}`}>空き</span>
                      </div>
                    </button>
                  );
                }
                // 予約あり: カード本体（クリックで展開）
                // 過去の日付の場合は背景色をグレーに変更
                const cardBgColor = isSelectedDatePast ? 'bg-gray-50' : 'bg-green-50';
                return (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      const nextExpanded = expandedTime === time ? null : time;
                      setExpandedTime(nextExpanded);
                      setScheduleSelectedTime(nextExpanded ? time : null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        const nextExpanded = expandedTime === time ? null : time;
                        setExpandedTime(nextExpanded);
                        setScheduleSelectedTime(nextExpanded ? time : null);
                      }
                    }}
                    className={`w-full p-3 rounded-md text-left transition-colors ${cardBgColor} ${expandedTime === time ? 'border-2 border-gray-400' : 'border border-transparent'}`}
                  >
                    <div className="grid grid-cols-[56px_1fr] gap-x-3 items-start">
                      <span className="text-sm text-gray-900 leading-5 font-semibold">{time}</span>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate leading-5">{info.patientName}</div>
                        {(() => {
                          const patient = allPatients.find(p => String(p.id) === String(info.patientId));
                          return (
                            <div className="text-xs text-gray-500 truncate">{patient?.nameRomaji || ''}</div>
                          );
                        })()}
                      </div>
                    </div>
                      <AnimatePresence initial={false}>
                        {expandedTime === time && (
                        <motion.div
                            key="expanded"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.28, ease: [0.25, 0.1, 0.25, 1] }}
                          className="overflow-hidden pt-2"
                          >
                          <div className="mt-1 grid grid-cols-[56px_1fr] gap-x-3 items-start">
                            <span className="block" />
                            <div className="text-sm text-gray-800 space-y-1">
                                {(() => {
                                  const patient = allPatients.find(p => String(p.id) === String(info.patientId));
                                  if (!patient) return null;
                                  return (
                                    <>
                                      {patient.patientNumber && (
                                        <div>{patient.patientNumber}</div>
                                      )}
                                      <div>{patient.address || '—'}</div>
                                      <div>{patient.patientPhoneNumber || patient.phoneNumber || '—'}</div>
                                      {patient.hasGuardian && (
                                        <div className="mt-2 p-3 border border-gray-300 rounded-md bg-gray-50">
                                          <div className="font-medium text-gray-700 mb-2">保護者情報</div>
                                          {patient.guardianName && (
                                            <div>
                                              {patient.guardianName}
                                              {patient.guardianRelationship && `（${patient.guardianRelationship}）`}
                                            </div>
                                          )}
                                          {patient.guardianNameRomaji && (
                                            <div>{patient.guardianNameRomaji}</div>
                                          )}
                                          {patient.guardianContact && (
                                            <div>{patient.guardianContact}</div>
                                          )}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            {/* 申し送り（診療: 登録 / 設定: 閲覧） + 予約キャンセル（横並び） — 展開に続いて遅めにフェードイン */}
                            <motion.div
                              className="mt-3 col-start-2 flex justify-end gap-2"
                              initial={{ opacity: 0, y: 6 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 4 }}
                              transition={{
                                opacity: { delay: 0.16, duration: 0.28, ease: 'easeOut' },
                                y: { delay: 0.16, duration: 0.28, ease: 'easeOut' },
                              }}
                            >
                              {/* 予約キャンセル（削除）ボタン：設定フローのみ、過去日は非表示 */}
                              {!isSelectedDatePast && schedulePanelSource !== 'workflow' && (
                                <button
                                  type="button"
                                  className="px-3 py-1.5 text-sm font-semibold rounded-md border border-red-300 text-red-700 bg-white hover:bg-red-50 hover:border-red-200 transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCancelConfirm({ time, patientName: info.patientName });
                                  }}
                                >
                                  予約キャンセル
                                </button>
                              )}

                              <button
                                type="button"
                                className="px-3 py-1.5 text-sm font-semibold rounded-md border border-green-400 text-green-700 bg-white hover:bg-green-50 hover:border-green-300 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // 患者IDを文字列に変換して設定（doctor_idと同様に）
                                  const patientIdStr = String(info.patientId);
                                  const currentState = useDashboardStore.getState();
                                  // デスクトップ: スケジュール右に申し送りを並べる（スケジュールは閉じない）
                                  // モバイル: 画面遷移として扱うため、スケジュールを閉じて申し送りを表示
                                  const nextState: any = {
                                    selectedPatientId: patientIdStr,
                                    showDetailPanel: true,
                                    detailPanelRevision: currentState.detailPanelRevision + 1, // 開き直し用にリビジョンを増やす
                                    // 予約登録パネルも閉じる
                                    showSchedulePatientListPanel: false,
                                  };
                                  if (isMobile) {
                                    nextState.showSchedulePanel = false;
                                    nextState.schedulePanelRevision = currentState.schedulePanelRevision + 1;
                                  }
                                  useDashboardStore.setState(nextState);
                                  // 展開状態をリセット
                                  setExpandedTime(null);
                                  setScheduleSelectedTime(null);
                                }}
                              >
                                {panelContext === 'settings' ? '閲覧' : '申し送り登録'}
                              </button>
                            </motion.div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      </div>
    </motion.div>
    </div>
    </>
  );
};


